/**
 * daemon-job-rpc-client
 *
 * Browser-side control for DAEMON jobs (job.kind = "daemon"): long-lived jobs the
 * continuum job-runner launches as transient systemd units via its convergence
 * loop. Unlike service-manager (which controls foreign OS services keyed on a
 * service id), this is job-runner territory: control is keyed on the continuum
 * job_uid directly, and the RPC is served by the JOB-RUNNER (jobrunner.v1), not a
 * service-manager agent.
 *
 * Mirrors the resolution pattern in service-manager-rpc-client.ts (findAgentForJob)
 * but queries the registry for processes implementing jobrunner.v1 and matches by
 * workerUid. The Go server side is jobrunner/jobcontrol.go
 * (FEATURE-20260717-job-runner-systemd-daemon-jobs).
 */

import GlobalClient from "../hermesclient/client";
import { ContentType, RpcFrameType } from "../hermesclient/proto/wsmessages";
import { nameMappingService } from "./NameMappingService";
import {
  asLogicalMailbox,
  asMailboxAddress,
  MailboxAddress,
} from "../hermesclient/types";

interface RegistryProcessDetail {
  processUid: string;
  mailboxAddress: string;
  workerUid?: string;
  metadata?: Record<string, string>;
}
interface ListProcessesResponse {
  processes?: RegistryProcessDetail[];
}

// Mirrors service-manager-rpc-client.ts::rpcCall — JSON body, error via
// frameType, response from resp.data.
async function rpcCall<TResp>(
  to: MailboxAddress,
  endPoint: string,
  request: Record<string, unknown>,
): Promise<TResp> {
  const client = GlobalClient.get();
  const body = new TextEncoder().encode(JSON.stringify(request));
  const resp = await client.rawRpcCall({
    to,
    endPoint,
    body,
    contentType: ContentType.Json,
  });
  const frameType = resp.header?.rpcHeader?.frameType;
  if (frameType === RpcFrameType.ErrorResponse) {
    const errorInfo = resp.header?.rpcHeader?.errorInfo;
    const msg =
      errorInfo?.message ||
      errorInfo?.errorNode?.frame?.message ||
      `RPC call to ${endPoint} failed`;
    throw new Error(msg);
  }
  if (!resp.data || resp.data.length === 0) {
    return {} as TResp;
  }
  return JSON.parse(new TextDecoder().decode(resp.data)) as TResp;
}

// findRunnerForJob resolves the mailbox of the job-runner that owns a given
// worker's daemon jobs, by querying the registry for processes implementing
// jobrunner.v1 and matching workerUid. Returns null if none is live (box offline
// / runner not registered). Mirrors findAgentForJob but for the runner, not a
// service-manager agent.
export async function findRunnerForJob(
  workerUid: string,
): Promise<MailboxAddress | null> {
  if (!workerUid) return null;
  const registryMailbox = await nameMappingService.resolve(
    asLogicalMailbox("registry"),
  );
  const resp = await rpcCall<ListProcessesResponse>(
    registryMailbox,
    "registry.v1.ListProcesses",
    { implements_rpc: "jobrunner.v1" },
  );
  for (const p of resp.processes || []) {
    const wu = p.workerUid || p.metadata?.["worker_uid"] || "";
    if (wu === workerUid) {
      return asMailboxAddress(p.mailboxAddress);
    }
  }
  return null;
}

interface JobControlResponse {
  success?: boolean;
  message?: string;
}

// startDaemonJob / stopDaemonJob / restartDaemonJob call the job-runner's
// jobrunner.v1 control RPCs directly on its mailbox, keyed on job_uid. The runner
// authorizes the caller per-job (continuum AuthorizeForJob at RankRun) and records
// intent (job.workerenabled) so the convergence loop agrees. Throws on
// denial/failure or a non-success response.
async function control(
  runnerMailbox: MailboxAddress,
  endPoint: string,
  jobUid: string,
): Promise<void> {
  const resp = await rpcCall<JobControlResponse>(runnerMailbox, endPoint, {
    job_uid: jobUid,
  });
  if (resp && resp.success === false) {
    throw new Error(resp.message || `${endPoint} failed`);
  }
}

export async function startDaemonJob(
  runnerMailbox: MailboxAddress,
  jobUid: string,
): Promise<void> {
  await control(runnerMailbox, "jobrunner.v1.StartJob", jobUid);
}

export async function stopDaemonJob(
  runnerMailbox: MailboxAddress,
  jobUid: string,
): Promise<void> {
  await control(runnerMailbox, "jobrunner.v1.StopJob", jobUid);
}

export async function restartDaemonJob(
  runnerMailbox: MailboxAddress,
  jobUid: string,
): Promise<void> {
  await control(runnerMailbox, "jobrunner.v1.RestartJob", jobUid);
}

// What a worker's runner says about itself (jobrunner.v1.Describe): whether its jobs run sandboxed as
// an execution user in a transient unit, and which accounts its host policy allows. The plugin
// monitor form offers a root worker's execution user from this. null when no runner is live for the
// worker, which is what "no runner" means. FEATURE-20261021-check-config-type-and-fileset-form.
export interface RunnerDescription {
  workerUid: string;
  sandboxed: boolean;
  allowedUsers: string[];
  /** false for a runner whose policy an executor daemon holds, or one from before Describe */
  policyKnown: boolean;
  version: string;
}

export async function describeRunner(workerUid: string): Promise<RunnerDescription | null> {
  const runnerMailbox = await findRunnerForJob(workerUid);
  if (!runnerMailbox) return null;
  const r = await rpcCall<{ workerUid?: string; sandboxed?: boolean; allowedUsers?: string[]; policyKnown?: boolean; version?: string }>(
    runnerMailbox,
    "jobrunner.v1.Describe",
    {},
  );
  return {
    workerUid: r.workerUid ?? workerUid,
    sandboxed: Boolean(r.sandboxed),
    allowedUsers: Array.isArray(r.allowedUsers) ? r.allowedUsers.map(String) : [],
    policyKnown: Boolean(r.policyKnown),
    version: r.version ?? "",
  };
}
